import type { JsonBalanceExtended } from 'src/store/shared/balances/types';
import type { JsonBalanceHistoryExtended } from 'src/store/shared/balance-history/types';

export type RowWarningInfo = {
  isWalletEmpty: boolean;
  isError: boolean;
  isWarning: boolean;
  message?: string;
  acctStatus?: number | undefined;
  shouldDisableSelection?: boolean;
};

/**
 * Single source of truth for "is this account an error", shared by the snapshot
 * and history transforms (which persist it as `hasError`) and by the UI fallback
 * below. The rule is intentionally independent of whether the account currently
 * holds assets:
 *   - status 0 / -2 (suspended / error) -> error
 *   - status 1 (enabled)               -> not an error (empty -> "Assets are missing")
 *   - otherwise                        -> error if it has a message or no assets
 */
export const computeAccountHasError = ({
  status,
  message,
  hasAssets,
}: {
  status?: number | null;
  message?: string | null;
  hasAssets: boolean;
}): boolean => {
  // Number(null) === 0 would misread a null status as a suspended account.
  const statusNumber = status == null ? NaN : Number(status);
  const hasMessage = typeof message === 'string' && message.trim().length > 0;

  if (statusNumber === 0 || statusNumber === -2) return true;
  if (statusNumber === 1) return false;
  return hasMessage || !hasAssets;
};

export const getRowWarningInfo = (
  item: JsonBalanceExtended | JsonBalanceHistoryExtended,
): RowWarningInfo => {
  const acctStatus = typeof item?.account?.status === 'number' ? item.account.status : undefined;

  const acctHasMessage =
    typeof item?.account?.message === 'string' && item.account.message.trim().length > 0;

  const acctIsStatusError = Number(acctStatus) === 0 || Number(acctStatus) === -2;

  const isWalletEmpty = Boolean(
    item?.isWallet &&
    Array.isArray(item.assets) &&
    item.assets.length === 0 &&
    !item.isAllTokensFiltered,
  );

  const isError =
    typeof item?.hasError === 'boolean'
      ? Boolean(item.hasError)
      : computeAccountHasError({
          status: acctStatus,
          message: item?.account?.message,
          hasAssets: Array.isArray(item.assets) && item.assets.length > 0,
        });

  const isWarning = isError || isWalletEmpty;

  const acctStatusNumber = Number(acctStatus);
  const isGenuinelyEmpty =
    Array.isArray(item.assets) && item.assets.length === 0 && !item.isAllTokensFiltered;

  // When every asset was filtered out of the view (e.g. by "ignore other
  // tokens"), the row shows only zeros and contributes nothing to the filtered
  // summary, so there is nothing meaningful to select.
  const shouldDisableSelection =
    isError ||
    isWalletEmpty ||
    Boolean(item.isAllTokensFiltered) ||
    (isGenuinelyEmpty && acctStatusNumber === 1);

  const rawMsg =
    item?.errorMessage?.trim() ||
    (acctHasMessage
      ? item?.account?.message?.trim()
      : acctIsStatusError
        ? Number(acctStatus) === 0
          ? 'Account suspended'
          : 'Account error'
        : undefined);

  const message = rawMsg ?? (isWalletEmpty ? 'No assets' : undefined);

  return {
    isWalletEmpty,
    isError,
    isWarning,
    message,
    acctStatus,
    shouldDisableSelection,
  };
};
